import { GROUPED_LEVEL_THRESHOLDS } from '@kwenta/sdk/constants'
import { PointsRank } from '@kwenta/sdk/types'
import { calculateLevelProgress, formatNumber } from '@kwenta/sdk/utils'
import { DialogOverlay } from '@reach/dialog'
import { useRouter } from 'next/router'
import { type FC, useCallback, useState } from 'react'
import styled from 'styled-components'

import { BaseCard } from 'components/Card/style'
import PointsRankAvatar from 'components/PointsRankAvatar'
import ProgressBar from 'components/ProgressBar'
import { Body } from 'components/Text'
import {
	FlexDivCol,
	FlexDivColCentered,
	FlexDivRow,
	FlexDivRowCentered,
} from 'components/layout/flex'
import ROUTES from 'constants/routes'
import useClickOutside from 'hooks/useClickOutside'
import PointsProgress from 'sections/dashboard/Points/PointsProgress'
import { PointsTabs } from 'sections/dashboard/Points/PointsTabs'
import { useAppSelector } from 'state/hooks'
import { selectPointsLevel, selectTotalPoints } from 'state/points/selectors'
import media from 'styles/media'

const PointsProfile: FC = () => {
	const router = useRouter()

	const points = useAppSelector(selectTotalPoints)
	const level = useAppSelector(selectPointsLevel)
	const progress = calculateLevelProgress(points)
	const [open, setOpen] = useState(false)

	const redirectToPoints = useCallback(() => {
		router.push(ROUTES.Dashboard.Points(PointsTabs.OVERVIEW))
	}, [router])

	const { ref } = useClickOutside(() => setOpen(false))

	return (
		<>
			<MenuContainer columnGap="10px" onClick={() => setOpen((x) => !x)}>
				<LeftContainer rowGap="7px">
					<PointsText weight="regular" color="secondary">
						{formatNumber(points, { maxDecimals: 0 })}
					</PointsText>
					<ProgressBar ratio={progress} height={6} />
				</LeftContainer>
				<PointsRankAvatar width={30} height={30} level={level} />
			</MenuContainer>
			{open && (
				<StyledDialogOverlay>
					<PanelContainer ref={ref}>
						<CardsContainer>
							<FlexDivCol rowGap="30px">
								<FlexDivColCentered rowGap="10px">
									<PointsRankAvatar
										width={90}
										height={90}
										level={level}
										className="avatar-with-shadow"
										onClick={redirectToPoints}
									/>
									<Body weight="semiBold" size="large">
										{level}
									</Body>
									<PointsProgress points={points} size="small" />
								</FlexDivColCentered>
								<FlexDivCol rowGap="5px">
									<FlexDivRow>
										<Body mono weight="regular" size="small" color="secondary">
											Level
										</Body>
										<Body mono weight="regular" size="small" color="secondary">
											Points
										</Body>
									</FlexDivRow>
									<LevelsContainer rowGap="5px">
										{GROUPED_LEVEL_THRESHOLDS.filter((g) => g.rank !== PointsRank.NPC).map(
											(rank, rankIndex) => {
												return (
													<div key={rank.rank}>
														<SectionSeparator />
														<FlexDivRow columnGap="10px">
															<PointsRankAvatar
																width={44}
																height={44}
																level={rank.levels[0].level}
															/>
															<FullWidthFlexDivCol columnGap="10px">
																{rank.levels.map((level, levelIndex) => {
																	let Container = LevelContainer
																	if (points >= level.start) {
																		Container = AchievedLevel
																	}
																	if (
																		points >= level.start &&
																		(points < rank.levels[levelIndex + 1]?.start ||
																			(!rank.levels[levelIndex + 1]?.start &&
																				points <
																					GROUPED_LEVEL_THRESHOLDS[rankIndex + 1]?.levels[0]
																						?.start))
																	) {
																		Container = CurrentLevel
																	}
																	return (
																		<Container key={level.level}>
																			<Body mono size="small">
																				{level.level}
																			</Body>
																			<Body mono size="small">
																				{formatNumber(level.start, { maxDecimals: 0 })}{' '}
																				{rankIndex === GROUPED_LEVEL_THRESHOLDS.length - 2
																					? '+'
																					: ''}
																			</Body>
																		</Container>
																	)
																})}
															</FullWidthFlexDivCol>
														</FlexDivRow>
													</div>
												)
											}
										)}
									</LevelsContainer>
								</FlexDivCol>
							</FlexDivCol>
						</CardsContainer>
					</PanelContainer>
				</StyledDialogOverlay>
			)}
		</>
	)
}

const MenuContainer = styled(FlexDivRowCentered)`
	cursor: pointer;
`
const PointsText = styled(Body)`
	text-align: end;
`
const LeftContainer = styled(FlexDivCol)`
	width: 100px;
`

const FullWidthFlexDivCol = styled(FlexDivCol)`
	width: 100%;
	justify-content: center;
`

const StyledDialogOverlay = styled(DialogOverlay)`
	z-index: 10;
	background: rgba(0, 0, 0, 0.5);
`

const PanelContainer = styled.div`
	z-index: 100;
	position: absolute;
	top: 14px;
	right: 280px;

	${media.lessThan('mdUp')`
		padding: 15px;
	`}

	${media.greaterThan('mdUp')`
		margin-top: 56px;
	`}
`

const LevelsContainer = styled(FlexDivCol)`
	max-height: calc(100vh - 360px);
	overflow-y: scroll;
`

const CardsContainer = styled(BaseCard)`
	display: flex;
	flex-direction: column;
	width: 280px;
	row-gap: 15px;
	border-radius: 8px;
	padding: 30px 15px 15px 15px;

	.avatar-with-shadow img {
		box-shadow: 0px 30px 90px 0px rgba(126, 125, 122, 0.6);
		cursor: pointer;
	}
`

const SectionSeparator = styled.div`
	margin-bottom: 5px;
	height: 1px;
	background-color: ${(props) => props.theme.colors.selectedTheme.newTheme.border.color};
`

const LevelContainer = styled(FlexDivRowCentered)`
	padding: 8px 10px;
	height: 31px;
	color: red;
`

const AchievedLevel = styled(LevelContainer)`
	p,
	span {
		color: #ffffff;
	}
`

const CurrentLevel = styled(LevelContainer)`
	background-color:  ${(props) => props.theme.colors.selectedTheme.button.highlight.fill};
	p,
	span {
		font-weight: 600;
	}
`

export default PointsProfile
